import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, LogIn } from 'lucide-react';
import { cn } from '../utils/cn';
import { Search } from './Search';

const navigation = [
  {
    name: 'About',
    href: '/about',
    submenu: [
      { name: 'Overview', href: '/about' },
      { name: 'Our Mission', href: '/about/mission' },
      { name: 'Our Team', href: '/about/team' },
      { name: 'Our Technology', href: '/about/technology' },
      { name: 'Our Facilities', href: '/about/facilities' },
    ],
  },
  {
    name: 'Services',
    href: '/services',
    submenu: [
      { name: 'Traumatic Brain Injuries', href: '/services/traumatic-brain-injuries' },
      { name: 'Dysautonomia', href: '/services/dysautonomia' },
      { name: 'Headaches', href: '/services/headaches' },
      { name: 'Functional Neurologic Disorder', href: '/services/fnd' },
      { name: 'Neurodevelopmental Disorders', href: '/services/neurodevelopmental' },
      { name: 'Movement Disorders', href: '/services/movement-disorders' },
    ],
  },
  {
    name: 'Patient Info',
    href: '/patient-info',
    submenu: [
      { name: 'New Patients', href: '/patient-info/new-patients' },
      { name: 'Insurance', href: '/patient-info/insurance' },
      { name: 'Patient Portal', href: '/patient-info/portal' },
    ],
  },
  {
    name: 'Resources',
    href: '/resources',
    submenu: [
      { name: 'Research', href: '/resources/research' },
      { name: 'FAQs', href: '/resources/faqs' },
      { name: 'Professional Organizations', href: '/resources/professional-organizations' },
    ],
  },
  { name: 'Contact', href: '/contact' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  return (
    <header className="bg-white">
      {/* Top bar */}
      <div className="bg-emory-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center space-x-4">
              <span>(470) 239-3602</span>
              <span>|</span>
              <a
                href="https://health.healow.com/juncosneuro"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center hover:text-emory-gold"
              >
                <LogIn className="h-4 w-4 mr-1" />
                Patient Portal
              </a>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/contact" className="hover:text-emory-gold">
                Our Location
              </Link>
              <span>|</span>
              <Search />
            </div>
          </div>
        </div>
      </div>

      <nav className="border-b border-emory-gray-200" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="https://mycw218.ecwcloud.com/portal26448/jsp/admin/getCroppedImage.jsp?id=0"
                  alt="Juncos Neuro Consulting Logo"
                  className="h-24 w-24 object-contain"
                />
                <span className="text-xl font-bold text-emory-blue">Juncos Neuro Consulting</span>
              </Link>
            </div>

            {/* Desktop navigation */}
            <div className="hidden lg:flex lg:gap-x-8">
              {navigation.map((item) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => setActiveSubmenu(item.name)}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  <Link
                    to={item.href}
                    className={cn(
                      'flex items-center gap-x-1 text-sm font-semibold leading-6 text-emory-gray-800 hover:text-emory-blue',
                      { 'text-emory-blue': activeSubmenu === item.name }
                    )}
                  >
                    {item.name}
                    {item.submenu && <ChevronDown className="h-4 w-4" />}
                  </Link>

                  {item.submenu && activeSubmenu === item.name && (
                    <div className="absolute left-0 top-full z-10 mt-1 w-48 rounded-md bg-white py-2 shadow-emory">
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          to={subitem.href}
                          className="block px-4 py-2 text-sm text-emory-gray-700 hover:bg-emory-gray-100 hover:text-emory-blue"
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md p-2.5 text-emory-gray-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <X className="h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-emory-gray-200">
            <div className="space-y-1 px-4 py-3">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className="block rounded-md px-3 py-2 text-base font-medium text-emory-gray-900 hover:bg-emory-gray-100 hover:text-emory-blue"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                  {item.submenu && (
                    <div className="ml-4 space-y-1">
                      {item.submenu.map((subitem) => (
                        <Link
                          key={subitem.name}
                          to={subitem.href}
                          className="block rounded-md px-3 py-2 text-sm font-medium text-emory-gray-500 hover:bg-emory-gray-100 hover:text-emory-blue"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {subitem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}